import { useState } from 'react'
import { Check, ChevronsUpDown, X, Star } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Badge } from '@/components/ui/badge'
import { useAircraftList } from '@/hooks/useAircraft'

interface SelectedAircraft {
  id: string
  tailNumber: string
  typeName?: string | null
  isPrimary: boolean
}

interface ContractAircraftSelectProps {
  value: SelectedAircraft[]
  onChange: (aircraft: SelectedAircraft[]) => void
  disabled?: boolean
}

export function ContractAircraftSelect({
  value,
  onChange,
  disabled = false,
}: ContractAircraftSelectProps) {
  const [open, setOpen] = useState(false)
  const { data: aircraft, isLoading } = useAircraftList()

  const selectedIds = new Set(value.map((a) => a.id))

  const handleSelect = (aircraftId: string) => {
    const selectedAircraft = aircraft?.find((a) => a.id === aircraftId)
    if (!selectedAircraft) return

    if (selectedIds.has(aircraftId)) {
      // Remove aircraft
      const newValue = value.filter((a) => a.id !== aircraftId)
      // If we removed the primary, make the first one primary
      if (newValue.length > 0 && value.find((a) => a.id === aircraftId)?.isPrimary) {
        newValue[0].isPrimary = true
      }
      onChange(newValue)
    } else {
      // Add aircraft
      const newAircraft: SelectedAircraft = {
        id: aircraftId,
        tailNumber: selectedAircraft.tail_number,
        typeName: selectedAircraft.aircraft_type_name,
        isPrimary: value.length === 0, // First one is primary by default
      }
      onChange([...value, newAircraft])
    }
  }

  const handleSetPrimary = (aircraftId: string) => {
    const newValue = value.map((a) => ({
      ...a,
      isPrimary: a.id === aircraftId,
    }))
    onChange(newValue)
  }

  const handleRemove = (aircraftId: string) => {
    const newValue = value.filter((a) => a.id !== aircraftId)
    // If we removed the primary, make the first one primary
    if (newValue.length > 0 && value.find((a) => a.id === aircraftId)?.isPrimary) {
      newValue[0].isPrimary = true
    }
    onChange(newValue)
  }

  return (
    <div className="space-y-3">
      {/* Selected aircraft badges */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((selected) => (
            <Badge
              key={selected.id}
              variant={selected.isPrimary ? 'default' : 'secondary'}
              className="flex items-center gap-1 pr-1"
            >
              {selected.isPrimary && (
                <Star className="h-3 w-3 fill-current" />
              )}
              <span className="font-mono">{selected.tailNumber}</span>
              {selected.typeName && (
                <span className="text-xs opacity-70">({selected.typeName})</span>
              )}
              <div className="flex items-center ml-1 border-l border-current/20 pl-1">
                {!selected.isPrimary && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 hover:bg-transparent"
                    onClick={() => handleSetPrimary(selected.id)}
                    disabled={disabled}
                    title="Set as primary"
                  >
                    <Star className="h-3 w-3" />
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0 hover:bg-transparent"
                  onClick={() => handleRemove(selected.id)}
                  disabled={disabled}
                  title="Remove"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </Badge>
          ))}
        </div>
      )}

      {/* Aircraft selector */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
            disabled={disabled}
          >
            {value.length === 0
              ? 'Select aircraft...'
              : `${value.length} aircraft selected`}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search by tail number..." />
            <CommandList>
              <CommandEmpty>
                {isLoading ? 'Loading aircraft...' : 'No aircraft found.'}
              </CommandEmpty>
              <CommandGroup>
                {aircraft?.map((ac) => (
                  <CommandItem
                    key={ac.id}
                    value={`${ac.tail_number} ${ac.aircraft_type_name || ''}`}
                    onSelect={() => handleSelect(ac.id)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        selectedIds.has(ac.id) ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <div className="flex flex-col">
                      <span className="font-mono font-medium">
                        {ac.tail_number}
                      </span>
                      {ac.aircraft_type_name && (
                        <span className="text-xs text-muted-foreground">
                          {ac.aircraft_type_name}
                        </span>
                      )}
                    </div>
                    {ac.owner_client_name && (
                      <span className="ml-auto text-xs text-muted-foreground">
                        {ac.owner_client_name}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <p className="text-xs text-muted-foreground">
          <Star className="inline h-3 w-3 mr-1" />
          Primary aircraft is highlighted. Click the star icon to change.
        </p>
      )}
    </div>
  )
}
